import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useCreatorResolver } from "@/organization"

import { useSetPageHeader } from "@/hooks/useSetPageHeader"

import {
  deleteNotebook,
  getNotebook,
  renameNotebook,
} from "../utils/notebooksApi"
import { type Notebook } from "../utils/notebookSnapshot"
import { DeleteNotebookDialog } from "./DeleteNotebookDialog"
import { EditableNotebookContent } from "./EditableNotebookContent"
import { NotebookSofiaPanel } from "./NotebookChat"
import { NotebookDetailHeader } from "./NotebookDetailHeader"
import { NotebookMenu } from "./NotebookMenu"
import { NotebookScheduleButton } from "./NotebookScheduleButton"
import { NotebookShareButton } from "./NotebookShareButton"
import { RenameNotebookDialog } from "./RenameNotebookDialog"

interface NotebookViewProps {
  organizationSlug: string
  workspaceId: string
  notebookId: string
  homeHref: string
  /** The Notebooks list, for the breadcrumb and after a delete. */
  notebooksHref: string
  currentHref: string
  onDeleted: () => void
}

/**
 * A saved notebook as a readable document: Sofia's title, summary, and
 * sections with the supporting tables and charts embedded, followed by any
 * remaining results. Older notebooks without a document render the cells alone.
 */
export function NotebookView({
  organizationSlug,
  workspaceId,
  notebookId,
  homeHref,
  notebooksHref,
  currentHref,
  onDeleted,
}: NotebookViewProps) {
  const [notebook, setNotebook] = useState<Notebook | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [documentLocked, setDocumentLocked] = useState(false)
  const [sofiaBusy, setSofiaBusy] = useState(false)
  const [hasUnsentMessages, setHasUnsentMessages] = useState(false)
  const [renaming, setRenaming] = useState(false)
  const [confirmingDelete, setConfirmingDelete] = useState(false)
  const [deletePending, setDeletePending] = useState(false)
  const { resolveCreator } = useCreatorResolver()
  const scopeKey = JSON.stringify([organizationSlug, workspaceId, notebookId])
  const activeScope = useRef({ key: scopeKey, generation: 0 })
  if (activeScope.current.key !== scopeKey) {
    activeScope.current = {
      key: scopeKey,
      generation: activeScope.current.generation + 1,
    }
  }
  const scopeGeneration = activeScope.current.generation

  useEffect(() => {
    activeScope.current.key = scopeKey
    return () => {
      if (activeScope.current.key === scopeKey) {
        activeScope.current.key = ""
      }
    }
  }, [scopeKey])

  const handleSaved = useCallback(
    (saved: Notebook) => {
      if (
        activeScope.current.key === scopeKey &&
        activeScope.current.generation === scopeGeneration &&
        saved.id === notebookId &&
        saved.workspace_id === workspaceId
      ) {
        setNotebook(saved)
      }
    },
    [notebookId, scopeGeneration, scopeKey, workspaceId],
  )

  const handleRename = useCallback(
    async (title: string) => {
      // A failed rename rejects, so the rename dialog stays open with the reason.
      const renamed = await renameNotebook(
        organizationSlug,
        workspaceId,
        notebookId,
        title,
      )
      setNotebook(renamed)
    },
    [notebookId, organizationSlug, workspaceId],
  )
  const handleDelete = useCallback(async () => {
    if (deletePending) {
      return
    }
    setDeletePending(true)
    try {
      await deleteNotebook(organizationSlug, workspaceId, notebookId)
      onDeleted()
    } catch (deleteError: unknown) {
      setDeletePending(false)
      setConfirmingDelete(false)
      setError(
        deleteError instanceof Error
          ? deleteError.message
          : "Could not delete the notebook.",
      )
    }
  }, [deletePending, notebookId, onDeleted, organizationSlug, workspaceId])
  const title = notebook?.document?.title ?? notebook?.title ?? null
  const header = useMemo(
    () => (
      <NotebookDetailHeader
        homeHref={homeHref}
        notebooksHref={notebooksHref}
        title={title}
        menu={
          notebook && !documentLocked && !sofiaBusy ? (
            <div className="flex items-center gap-2">
              <NotebookScheduleButton
                key={`schedule:${organizationSlug}:${workspaceId}:${notebookId}`}
                notebook={notebook}
                organizationSlug={organizationSlug}
                workspaceId={workspaceId}
                currentHref={currentHref}
                onNotebookReloaded={handleSaved}
              />
              <NotebookShareButton
                key={`${organizationSlug}:${workspaceId}:${notebookId}`}
                notebookId={notebook.id}
                organizationSlug={organizationSlug}
                workspaceId={workspaceId}
              />
              <NotebookMenu
                notebookId={notebook.id}
                href={currentHref}
                onRename={() => setRenaming(true)}
                onDelete={() => setConfirmingDelete(true)}
              />
            </div>
          ) : null
        }
      />
    ),
    [
      currentHref,
      handleSaved,
      documentLocked,
      sofiaBusy,
      homeHref,
      notebook,
      notebookId,
      notebooksHref,
      title,
      organizationSlug,
      workspaceId,
    ],
  )
  useSetPageHeader(header)

  useEffect(() => {
    const controller = new AbortController()
    setNotebook(null)
    setError(null)
    setSofiaBusy(false)
    setHasUnsentMessages(false)
    setDocumentLocked(false)
    getNotebook(organizationSlug, workspaceId, notebookId, controller.signal)
      .then((loaded) => {
        if (!controller.signal.aborted) {
          setNotebook(loaded)
        }
      })
      .catch((loadError: unknown) => {
        if (!controller.signal.aborted) {
          setError(
            loadError instanceof Error
              ? loadError.message
              : "Could not load the notebook.",
          )
        }
      })
    return () => controller.abort()
  }, [notebookId, organizationSlug, workspaceId])

  if (error) {
    return (
      <div className="flex h-full items-center justify-center p-8">
        <div className="text-center">
          <p className="mb-2 text-2xl">Failed to load notebook</p>
          <p className="text-lg text-coval-text-muted">{error}</p>
        </div>
      </div>
    )
  }

  if (!notebook) {
    return (
      <div className="h-full overflow-y-auto px-6 py-6">
        <div
          className="mx-auto h-72 w-full max-w-5xl animate-pulse rounded-lg border border-coval-border bg-surface-default"
          role="status"
          aria-label="Loading notebook"
          aria-live="polite"
        />
      </div>
    )
  }

  return (
    <div className="h-full overflow-y-auto px-6 py-6">
      <DeleteNotebookDialog
        open={confirmingDelete}
        title={title ?? "this notebook"}
        pending={deletePending}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={() => void handleDelete()}
      />
      <RenameNotebookDialog
        open={renaming}
        title={title ?? ""}
        onClose={() => setRenaming(false)}
        onRename={handleRename}
      />
      <div className="min-h-0">
        <div className="min-w-0">
          <EditableNotebookContent
            key={`${organizationSlug}:${workspaceId}:${notebook.id}`}
            onSaved={handleSaved}
            onLockChange={setDocumentLocked}
            notebook={notebook}
            organizationSlug={organizationSlug}
            workspaceId={workspaceId}
            creatorLabel={resolveCreator(notebook.created_by)}
            busy={sofiaBusy}
            hasUnsentMessages={hasUnsentMessages}
          />
        </div>
        {notebook.editing_enabled && (
          <NotebookSofiaPanel
            path={currentHref}
            key={`${organizationSlug}:${workspaceId}:${notebook.id}`}
            notebook={notebook}
            organizationSlug={organizationSlug}
            workspaceId={workspaceId}
            locked={documentLocked}
            onSaved={handleSaved}
            onBusyChange={setSofiaBusy}
            onUnsentChange={setHasUnsentMessages}
          />
        )}
      </div>
    </div>
  )
}
